import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
  Switch,
  FormControlLabel
} from "@mui/material";
import { 
  Delete as DeleteIcon, 
  Edit as EditIcon, 
  Add as AddIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { 
  getAllTests, 
  getTestById, 
  addQuestionToTest, 
  updateQuestion, 
  deleteQuestion,
  createTest,
  updateTest,
  deleteTest,
  resetTestStates,
  clearError
} from "../../redux/testSlice";
import AdminSideBar from "../../common/AdminSideBar/AdminSideBar";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SurveySettings = () => {
  const dispatch = useDispatch();
  const { 
    tests, 
    currentTestQuestions, 
    loading, 
    error,
    createTestSuccess,
    updateTestSuccess,
    deleteTestSuccess,
    addQuestionSuccess,
    updateQuestionSuccess,
    deleteQuestionSuccess
  } = useSelector((state) => state.tests);

  const [selectedTestId, setSelectedTestId] = useState(null);
  
  // Dialog States
  const [openTestDialog, setOpenTestDialog] = useState(false);
  const [openQuestionDialog, setOpenQuestionDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState({ open: false, type: "", id: "" });
  
  // Data States
  const [isEditingTest, setIsEditingTest] = useState(false);
  const [testData, setTestData] = useState({
    name: "",
    description: "",
    instructions: "",
    imageUrl: "",
    type: "MENTAL_WELLBEING",
    isActive: true,
    questionText: "",
    questionNumber: 1,
    questionType: "MULTIPLE_CHOICE",
    options: [
      { text: "Option 1", value: 0 }, 
      { text: "Option 2", value: 1 }
    ],
    minScale: 1,
    maxScale: 5,
    minLabel: "",
    maxLabel: ""
  });

  const [isEditingQuestion, setIsEditingQuestion] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [questionData, setQuestionData] = useState({
    questionText: "",
    questionNumber: 1,
    questionType: "MULTIPLE_CHOICE",
    options: [],
    minScale: 1,
    maxScale: 5,
    minLabel: "",
    maxLabel: ""
  });

  useEffect(() => {
    dispatch(getAllTests());
  }, [dispatch]);

  useEffect(() => {
    if (createTestSuccess || updateTestSuccess || deleteTestSuccess || 
        addQuestionSuccess || updateQuestionSuccess || deleteQuestionSuccess) {
      
      let msg = "";
      if (createTestSuccess) msg = "Test created!";
      else if (updateTestSuccess) msg = "Test updated!";
      else if (deleteTestSuccess) msg = "Test deleted!";
      else if (addQuestionSuccess) msg = "Question added!";
      else if (updateQuestionSuccess) msg = "Question updated!";
      else if (deleteQuestionSuccess) msg = "Question deleted!";
      
      toast.success(msg);
      setOpenTestDialog(false);
      setOpenQuestionDialog(false);
      setOpenDeleteDialog({ open: false, type: "", id: "" });
      
      dispatch(resetTestStates());
      dispatch(getAllTests());
      
      if (selectedTestId && !deleteTestSuccess) {
        dispatch(getTestById(selectedTestId));
      } else if (deleteTestSuccess) {
        setSelectedTestId(null);
      }
    }
  }, [createTestSuccess, updateTestSuccess, deleteTestSuccess, addQuestionSuccess, updateQuestionSuccess, deleteQuestionSuccess, dispatch, selectedTestId]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleSelectTest = (id) => {
    setSelectedTestId(id);
    dispatch(getTestById(id));
  };

  // Test Actions
  const handleOpenAddTest = () => {
    dispatch(resetTestStates());
    setIsEditingTest(false);
    setTestData({ 
      name: "", 
      description: "Onboarding Survey", 
      instructions: "Please answer all questions according to your current state.", 
      type: "MENTAL_WELLBEING", 
      isActive: true,
      // Initial Question Fields
      questionText: "",
      questionNumber: 1,
      questionType: "MULTIPLE_CHOICE",
      options: [
        { text: "Option 1", value: 0 }, 
        { text: "Option 2", value: 1 }
      ],
      minScale: 1,
      maxScale: 5,
      minLabel: "",
      maxLabel: ""
    });
    setOpenTestDialog(true);
  };

  const handleOpenEditTest = async (test) => {
    setIsEditingTest(true);
    setSelectedTestId(test._id);
    
    try {
      // Fetch fresh test data to ensure we have questions
      const fullTest = await dispatch(getTestById(test._id)).unwrap();
      const firstQuestion = (fullTest.questions && fullTest.questions[0]) || {};
      
      setTestData({
        name: fullTest.name,
        description: fullTest.description,
        instructions: fullTest.instructions || "",
        type: "MENTAL_WELLBEING", // Always enforce MENTAL_WELLBEING in this view
        isActive: fullTest.isActive,
        questionText: firstQuestion.questionText || fullTest.name,
        questionNumber: firstQuestion.questionNumber || 1,
        questionType: firstQuestion.questionType || "MULTIPLE_CHOICE",
        options: (firstQuestion.options && firstQuestion.options.length > 0) 
          ? firstQuestion.options.map(opt => ({ text: opt.text, value: opt.value })) 
          : [{ text: "Option 1", value: 0 }, { text: "Option 2", value: 1 }],
        minScale: firstQuestion.minScale || 1,
        maxScale: firstQuestion.maxScale || 5,
        minLabel: firstQuestion.minLabel || "",
        maxLabel: firstQuestion.maxLabel || ""
      });
      setOpenTestDialog(true);
    } catch (err) {
      toast.error("Failed to load survey details.");
    }
  };

  const handleSaveTest = async () => {
    try {
      if (isEditingTest) {
        // Update Test metadata
        const testPayload = {
          name: testData.questionText,
          description: testData.questionText,
          instructions: testData.instructions,
          type: testData.type,
          isActive: testData.isActive,
          imageUrl: ""
        };
        
        await dispatch(updateTest({ testId: selectedTestId, testData: testPayload })).unwrap();

        // Also update the first question if it exists
        if (currentTestQuestions.length > 0) {
          const firstQ = currentTestQuestions[0];
          const questionPayload = {
            questionText: testData.questionText,
            questionNumber: Number(testData.questionNumber),
            questionType: testData.questionType,
            options: (testData.questionType === "MULTIPLE_CHOICE" || testData.questionType === "YES_NO") 
              ? testData.options.map(opt => ({ text: opt.text, value: Number(opt.value) })) 
              : [],
            minScale: Number(testData.minScale) || 1,
            maxScale: Number(testData.maxScale) || 5,
            minLabel: testData.minLabel || "",
            maxLabel: testData.maxLabel || ""
          };
          await dispatch(updateQuestion({ testId: selectedTestId, questionId: firstQ._id, questionData: questionPayload })).unwrap();
        }
        await dispatch(getTestById(selectedTestId)).unwrap();
      } else {
        // Validation
        // Create Test first - using exact schema from AdminTestsPage.jsx
        const testPayload = {
          name: testData.name || "New Survey",
          description: testData.description || "New Survey Description",
          instructions: testData.instructions || "Please answer all questions.",
          type: "MENTAL_WELLBEING",
          imageUrl: testData.imageUrl || ""
        };

        const createResponse = await dispatch(createTest(testPayload)).unwrap();
        const createdTest = createResponse?.data || createResponse;
        const newTestId = createdTest?._id;
        
        if (!newTestId) {
          toast.error("Survey created but test id was not returned.");
          return;
        }

        // Add the first question immediately
        const questionPayload = {
          questionText: testData.questionText,
          questionNumber: Number(testData.questionNumber) || 1,
          questionType: testData.questionType,
          options: (testData.questionType === "MULTIPLE_CHOICE" || testData.questionType === "YES_NO") 
            ? testData.options.map(opt => ({ text: opt.text, value: Number(opt.value) })) 
            : [],
          minScale: Number(testData.minScale) || 1,
          maxScale: Number(testData.maxScale) || 5,
          minLabel: testData.minLabel || "",
          maxLabel: testData.maxLabel || ""
        };

        await dispatch(addQuestionToTest({ testId: newTestId, questionData: questionPayload })).unwrap();
        setSelectedTestId(newTestId);
        await dispatch(getTestById(newTestId)).unwrap();
      }
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to save survey.");
    }
  };

  const handleDeleteTestClick = (id) => {
    setOpenDeleteDialog({ open: true, type: "test", id });
  };

  // Question Actions
  const handleOpenAddQuestion = () => {
    setIsEditingQuestion(false);
    setQuestionData({
      questionText: "",
      questionNumber: currentTestQuestions.length + 1,
      questionType: "MULTIPLE_CHOICE",
      options: [
        { text: "Option 1", value: 0 }, 
        { text: "Option 2", value: 1 }
      ],
      minScale: 1,
      maxScale: 5,
      minLabel: "",
      maxLabel: ""
    });
    setOpenQuestionDialog(true);
  };

  const handleOpenEditQuestion = (q) => {
    setIsEditingQuestion(true);
    setCurrentQuestionId(q._id);
    setQuestionData({
      questionText: q.questionText,
      questionNumber: q.questionNumber,
      questionType: q.questionType,
      options: q.options ? q.options.map(opt => ({ text: opt.text || "", value: opt.value ?? 0 })) : [],
      minScale: q.minScale || 1,
      maxScale: q.maxScale || 5,
      minLabel: q.minLabel || "",
      maxLabel: q.maxLabel || ""
    });
    setOpenQuestionDialog(true);
  };

  const handleSaveQuestion = async () => {
    if (!selectedTestId) {
      toast.error("Please select a survey first.");
      return;
    }

    // Ensure all numeric types and mandatory fields as per Swagger
    const payload = {
      ...questionData,
      questionNumber: Number(questionData.questionNumber),
      minScale: Number(questionData.minScale) || 1,
      options: (questionData.questionType === "MULTIPLE_CHOICE" || questionData.questionType === "YES_NO") 
        ? questionData.options.map(opt => ({ text: opt.text, value: Number(opt.value) })) 
        : [],
      maxScale: Number(questionData.maxScale) || 0,
      minLabel: questionData.minLabel || "string", // Matching Swagger "string" placeholder if empty
      maxLabel: questionData.maxLabel || "string"
    };

    try {
      if (isEditingQuestion) {
        await dispatch(updateQuestion({ testId: selectedTestId, questionId: currentQuestionId, questionData: payload })).unwrap();
      } else {
        await dispatch(addQuestionToTest({ testId: selectedTestId, questionData: payload })).unwrap();
      }
      await dispatch(getTestById(selectedTestId)).unwrap();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to save question.");
    }
  };

  const handleDeleteQuestionClick = (id) => {
    setOpenDeleteDialog({ open: true, type: "question", id });
  };

  const handleConfirmDelete = () => {
    if (openDeleteDialog.type === "test") {
      dispatch(deleteTest(openDeleteDialog.id));
    } else {
      dispatch(deleteQuestion({ testId: selectedTestId, questionId: openDeleteDialog.id }));
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      <div className="md:shrink-0 md:mr-6">
        <AdminSideBar />
      </div>
      <div className="flex-1 p-4 md:p-6 lg:p-8">
        <Box>
          <Typography variant="h5" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Onboarding Survey Management 
        <Typography variant="caption" sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', px: 1, borderRadius: 1 }}>Mental Wellbeing</Typography>
      </Typography>

      <Grid container spacing={4}>
        {/* Test List Panel */}
        <Grid item xs={12} md={4}>
          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" color="textSecondary">Active Surveys</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={handleOpenAddTest}>Create New Survey</Button>
          </Box>
          <Stack spacing={2}>
            {loading && !tests.length ? (
              <CircularProgress size={24} />
            ) : tests.filter(t => t.type === "MENTAL_WELLBEING").length > 0 ? (
              tests.filter(t => t.type === "MENTAL_WELLBEING").map((test) => (
                <Card 
                  key={test._id} 
                  sx={{ 
                    cursor: "pointer",
                    border: selectedTestId === test._id ? "2px solid #5a4fcf" : "1px solid #eee",
                    boxShadow: selectedTestId === test._id ? "0 4px 12px rgba(90,79,207,0.1)" : "none",
                    position: "relative"
                  }}
                  onClick={() => handleSelectTest(test._id)}
                >
                  <CardContent sx={{ py: "16px !important" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Box>
                        <Typography variant="subtitle1" fontWeight="600">{test.name}</Typography>
                        <Typography variant="caption" color="textSecondary" display="block">Onboarding Survey • {test.isActive ? "Active" : "Inactive"}</Typography>
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenEditTest(test); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteTestClick(test._id); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Box p={3} textAlign="center" bgcolor="#f5f5f5" borderRadius="8px">
                <Typography variant="body1" fontWeight="500">
                    No onboarding surveys found.
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Click "Create New Survey" to add your first onboarding questionnaire.
                  </Typography>
              </Box>
            )}
          </Stack>
        </Grid>

        {/* Questions Panel */}
        <Grid item xs={12} md={8}>
          {selectedTestId ? (
            <Box>
              <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6">Survey Questions</Typography>
                  <Typography variant="caption" color="textSecondary">Managing questions for: {tests.find(t => t._id === selectedTestId)?.name}</Typography>
                </Box>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddQuestion}
                  sx={{ borderRadius: "8px" }}
                >
                  Add New Question
                </Button>
              </Box>

              {loading ? (
                <CircularProgress />
              ) : (
                <List sx={{ bgcolor: "white", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  {currentTestQuestions.length > 0 ? (
                    currentTestQuestions.map((q, idx) => (
                      <React.Fragment key={q._id}>
                        <ListItem
                          secondaryAction={
                            <Box>
                              <IconButton onClick={() => handleOpenEditQuestion(q)} size="small" color="primary">
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={() => handleDeleteQuestionClick(q._id)} size="small" color="error">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText 
                            primary={<Typography fontWeight="500">{q.questionNumber}. {q.questionText}</Typography>}
                            secondary={
                              <Typography variant="caption" color="textSecondary">
                                {q.questionType} 
                                {q.options && q.options.length > 0 && ` • ${q.options.length} options`}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {idx < currentTestQuestions.length - 1 && <Divider />}
                      </React.Fragment>
                    ))
                  ) : (
                    <Box p={4} textAlign="center">
                      <Typography color="textSecondary">No questions in this test yet.</Typography>
                    </Box>
                  )}
                </List>
              )}
            </Box>
          ) : (
            <Box sx={{ height: "300px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8f9fa", borderRadius: "12px", p: 4, textAlign: "center" }}>
              <Typography color="textSecondary">Please select a test from the left to manage its questions.</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Test Add/Edit Dialog */}
      <Dialog open={openTestDialog} onClose={() => setOpenTestDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditingTest ? "Edit Survey Question" : "Create New Onboarding Survey"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="primary">Unified Survey & Question Detail</Typography>
            <TextField 
              label="Survey Name" 
              fullWidth 
              size="small"
              value={testData.name} 
              onChange={(e) => setTestData({...testData, name: e.target.value})} 
              placeholder="e.g. Onboarding Survey"
              required
            />
            <TextField 
              label="Question Text" 
              fullWidth 
              multiline 
              rows={2} 
              value={testData.questionText} 
              onChange={(e) => setTestData({...testData, questionText: e.target.value})} 
              placeholder="Enter the first question here..."
              required
            />
            <Box display="flex" gap={1} alignItems="center">
              <FormControl size="small" sx={{ width: 140 }}>
                <InputLabel>Survey Type</InputLabel>
                <Select 
                  value={testData.type} 
                  label="Survey Type" 
                  onChange={(e) => setTestData({...testData, type: e.target.value})}
                >
                  <MenuItem value="MENTAL_WELLBEING">Mental Wellbeing</MenuItem>
                </Select>
              </FormControl>
              <TextField 
                label="Number" 
                type="number" 
                size="small" 
                value={testData.questionNumber} 
                onChange={(e) => setTestData({...testData, questionNumber: e.target.value})} 
                sx={{ width: 80 }} 
              />
              <FormControl size="small" sx={{ flexGrow: 1 }}>
                <InputLabel>Question Type</InputLabel>
                <Select value={testData.questionType} label="Question Type" onChange={(e) => setTestData({...testData, questionType: e.target.value})}>
                  <MenuItem value="MULTIPLE_CHOICE">Multiple Choice</MenuItem>
                  <MenuItem value="YES_NO">Yes/No</MenuItem>
                  <MenuItem value="SCALE">Scale</MenuItem>
                  <MenuItem value="TEXT">Text</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Switch size="small" checked={testData.isActive} onChange={(e) => setTestData({...testData, isActive: e.target.checked})} />}
                label="Active"
                sx={{ ml: 1 }}
              />
            </Box>
            
            {(testData.questionType === "MULTIPLE_CHOICE" || testData.questionType === "YES_NO") && (
              <Box>
                <Typography variant="subtitle2" mb={1} mt={1}>Options</Typography>
                {testData.options.map((opt, idx) => (
                  <Box key={idx} display="flex" gap={1} mb={1}>
                    <TextField 
                      label="Text"
                      fullWidth 
                      size="small" 
                      value={opt.text} 
                      onChange={(e) => {
                        const next = [...testData.options];
                        next[idx] = { ...next[idx], text: e.target.value };
                        setTestData({...testData, options: next});
                      }} 
                    />
                    <TextField 
                      label="Value"
                      type="number"
                      size="small" 
                      sx={{ width: 80 }}
                      value={opt.value} 
                      onChange={(e) => {
                        const next = [...testData.options];
                        next[idx] = { ...next[idx], value: e.target.value };
                        setTestData({...testData, options: next});
                      }} 
                    />
                    <IconButton size="small" color="error" onClick={() => {
                      const next = [...testData.options];
                      next.splice(idx, 1);
                      setTestData({...testData, options: next});
                    }}><DeleteIcon fontSize="inherit" /></IconButton>
                  </Box>
                ))}
                <Button startIcon={<AddIcon />} size="small" onClick={() => setTestData({...testData, options: [...testData.options, { text: "", value: testData.options.length }]})}>Add Option</Button>
              </Box>
            )}

            {testData.questionType === "SCALE" && (
              <Grid container spacing={2} mt={1}>
                <Grid item xs={6}><TextField label="Min Value" type="number" size="small" fullWidth value={testData.minScale} onChange={(e) => setTestData({...testData, minScale: e.target.value})} /></Grid>
                <Grid item xs={6}><TextField label="Max Value" type="number" size="small" fullWidth value={testData.maxScale} onChange={(e) => setTestData({...testData, maxScale: e.target.value})} /></Grid>
                <Grid item xs={6}><TextField label="Min Label" size="small" fullWidth value={testData.minLabel} onChange={(e) => setTestData({...testData, minLabel: e.target.value})} /></Grid>
                <Grid item xs={6}><TextField label="Max Label" size="small" fullWidth value={testData.maxLabel} onChange={(e) => setTestData({...testData, maxLabel: e.target.value})} /></Grid>
              </Grid>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTestDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTest} disabled={loading}>{loading ? <CircularProgress size={20} /> : (isEditingTest ? "Update" : "Create")}</Button>
        </DialogActions>
      </Dialog>

      {/* Question Add/Edit Dialog */}
      <Dialog open={openQuestionDialog} onClose={() => setOpenQuestionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditingQuestion ? "Edit Question" : "Add New Question"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Question Text" fullWidth multiline rows={2} value={questionData.questionText} onChange={(e) => setQuestionData({...questionData, questionText: e.target.value})} />
            <Box display="flex" gap={2}>
              <TextField label="Number" type="number" value={questionData.questionNumber} onChange={(e) => setQuestionData({...questionData, questionNumber: e.target.value})} sx={{ width: 100 }} />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={questionData.questionType} label="Type" onChange={(e) => setQuestionData({...questionData, questionType: e.target.value})}>
                  <MenuItem value="MULTIPLE_CHOICE">Multiple Choice</MenuItem>
                  <MenuItem value="YES_NO">Yes/No</MenuItem>
                  <MenuItem value="SCALE">Scale</MenuItem>
                  <MenuItem value="TEXT">Text</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {(questionData.questionType === "MULTIPLE_CHOICE" || questionData.questionType === "YES_NO") && (
              <Box>
                <Typography variant="subtitle2" mb={1}>Options</Typography>
                {questionData.options.map((opt, idx) => (
                  <Box key={idx} display="flex" gap={1} mb={1}>
                    <TextField 
                      label="Text"
                      fullWidth 
                      size="small" 
                      value={opt.text} 
                      onChange={(e) => {
                        const next = [...questionData.options];
                        next[idx] = { ...next[idx], text: e.target.value };
                        setQuestionData({...questionData, options: next});
                      }} 
                    />
                    <TextField 
                      label="Value"
                      type="number"
                      size="small" 
                      sx={{ width: 80 }}
                      value={opt.value} 
                      onChange={(e) => {
                        const next = [...questionData.options];
                        next[idx] = { ...next[idx], value: e.target.value };
                        setQuestionData({...questionData, options: next});
                      }} 
                    />
                    <IconButton size="small" color="error" onClick={() => {
                      const next = [...questionData.options];
                      next.splice(idx, 1);
                      setQuestionData({...questionData, options: next});
                    }}><DeleteIcon fontSize="inherit" /></IconButton>
                  </Box>
                ))}
                <Button startIcon={<AddIcon />} size="small" onClick={() => setQuestionData({...questionData, options: [...questionData.options, { text: "", value: questionData.options.length }]})}>Add Option</Button>
              </Box>
            )}
            {questionData.questionType === "SCALE" && (
              <Grid container spacing={2}>
                <Grid item xs={6}><TextField label="Min Value" type="number" fullWidth value={questionData.minScale} onChange={(e) => setQuestionData({...questionData, minScale: e.target.value})} /></Grid>
                <Grid item xs={6}><TextField label="Max Value" type="number" fullWidth value={questionData.maxScale} onChange={(e) => setQuestionData({...questionData, maxScale: e.target.value})} /></Grid>
                <Grid item xs={6}><TextField label="Min Label" fullWidth value={questionData.minLabel} onChange={(e) => setQuestionData({...questionData, minLabel: e.target.value})} /></Grid>
                <Grid item xs={6}><TextField label="Max Label" fullWidth value={questionData.maxLabel} onChange={(e) => setQuestionData({...questionData, maxLabel: e.target.value})} /></Grid>
              </Grid>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQuestionDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveQuestion} disabled={loading}>{loading ? <CircularProgress size={20} /> : (isEditingQuestion ? "Save" : "Add")}</Button>
        </DialogActions>
      </Dialog>

      {/* Universal Delete Dialog */}
      <Dialog open={openDeleteDialog.open} onClose={() => setOpenDeleteDialog({ ...openDeleteDialog, open: false })}>
        <DialogTitle>Delete {openDeleteDialog.type === "test" ? "Test" : "Question"}</DialogTitle>
        <DialogContent>Are you sure you want to delete this {openDeleteDialog.type}? This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog({ ...openDeleteDialog, open: false })}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={loading}>{loading ? <CircularProgress size={20} /> : "Delete"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </div>
    </div>
  );
};

export default SurveySettings;
